import React, { useState } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  TablePagination,
  makeStyles,
  Typography,
  Box,
  IconButton,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import EditIcon from '@material-ui/icons/Edit'
import DeleteIcon from '@material-ui/icons/Delete'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useTranslate } from 'react-admin'
import useQueryParams from '../../hooks/useQueryParams'
import useHistoryPush from '../../hooks/useHistoryPush'
import getOrgPlayers from '../../Apis/player/getOrgPlayers'
import deletePlayer from '../../Apis/player/deletePlayer'
import QueryKeys from '../../utils/constants/queryKeys'
import LoaderDots from '../../components/loaders/LoaderDots/LoaderDots'
import COLORS from '../../utils/constants/colors'
import AddButton from '../../components/ui/AddButton/AddButton'
import PlayerCreateDrawer from './PlayerCreateDrawer'
import DeleteModal from '../../components/DeleteModal/DeleteModal'
import ORGANISATION_TYPES from '../../utils/constants/OrganisationTypes'
import localStorageIds from '../../utils/constants/localStorageIds'
import LeaguePlayersPage from './LeaguePlayersPage'
import USER_PERMISSIONS from '../../utils/constants/userPermissions'
import IsPermitted from '../../components/IsPermitted/IsPermitted'

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(2),
    padding: '2%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  searchField: {
    width: '100%',
    maxWidth: 400,
  },
  table: {
    minWidth: 650,
  },
  tableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  loaderContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
  },
  noData: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
}))

const OrgLevelPlayersPage = () => {
  const classes = useStyles()
  const translate = useTranslate()
  const queryParams = useQueryParams()
  const queryClient = useQueryClient()
  const { redirect } = useHistoryPush()
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [selectedPlayer, setSelectedPlayer] = useState(null)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [playerToDelete, setPlayerToDelete] = useState(null)

  const { data, isLoading } = useQuery(
    [QueryKeys.GET_ORG_PLAYERS, queryParams?.org, searchQuery, page, rowsPerPage],
    () => getOrgPlayers({
      orgId: queryParams?.org,
      Q: searchQuery || undefined,
      page: page + 1,
      perPage: rowsPerPage,
    }),
    {
      enabled: Boolean(queryParams?.org),
      keepPreviousData: true,
    }
  )

  const deletePlayerMutation = useMutation(
    (playerId) => deletePlayer(playerId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKeys.GET_ORG_PLAYERS])
        setIsDeleteModalOpen(false)
        setPlayerToDelete(null)
      },
    }
  )

  const handleEditPlayer = (playerId) => {
    const player = players.find((p) => p.id === playerId)
    setSelectedPlayer(player)
    setIsDrawerOpen(true)
  }

  const handleDeletePlayer = (playerId) => {
    const player = players.find((p) => p.id === playerId)
    setPlayerToDelete(player)
    setIsDeleteModalOpen(true)
  }

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return '-'
    const today = new Date()
    const birthDate = new Date(dateOfBirth)
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }
    return age
  }

  const getTeamNames = (player) => {
    if (!player.teams || player.teams.length === 0) return '-'
    return player.teams.map((t) => t.name).join(', ')
  }

  const getPositions = (player) => {
    if (!player.personalData?.positions || player.personalData.positions.length === 0) return '-'
    return player.personalData.positions.map((p) => p.name).join(', ')
  }

  if (isLoading) {
    return (
      <Box className={classes.loaderContainer}>
        <LoaderDots style={{ fill: COLORS.orange, width: 70, height: 70 }} />
      </Box>
    )
  }

  const players = data?.players || []
  const totalCount = data?.metadata?.totalCount || 0

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <TextField
          className={classes.searchField}
          placeholder="Search players..."
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setPage(0) }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <IsPermitted permission={USER_PERMISSIONS.UPDATE_TEAM}>
          <AddButton
            label={translate('ra.buttons.addPlayer')}
            onClick={() => { setSelectedPlayer(null); setIsDrawerOpen(true) }}
            filled
          />
        </IsPermitted>
      </div>

      <TableContainer component={Paper}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Teams</TableCell>
              <TableCell>Height</TableCell>
              <TableCell>Age</TableCell>
              <TableCell>Positions</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {players.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography className={classes.noData} variant="body1">
                    No players found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              players.map((player) => (
                <TableRow
                  key={player.id}
                  className={classes.tableRow}
                  onClick={() => redirect(`/competition/stats/player/${player.id}`)}
                >
                  <TableCell>
                    {player.personalData?.firstName} {player.personalData?.lastName}
                  </TableCell>
                  <TableCell>{getTeamNames(player)}</TableCell>
                  <TableCell>{player.personalData?.height ? `${player.personalData.height} cm` : '-'}</TableCell>
                  <TableCell>{calculateAge(player.personalData?.dateOfBirth)}</TableCell>
                  <TableCell>{getPositions(player)}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleEditPlayer(player.id) }}
                      aria-label="edit"
                    >
                      <EditIcon fontSize="small" style={{ fill: COLORS.orange }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleDeletePlayer(player.id) }}
                      aria-label="delete"
                    >
                      <DeleteIcon fontSize="small" style={{ fill: COLORS.purple }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
      />

      <PlayerCreateDrawer
        open={isDrawerOpen}
        onClose={() => { setIsDrawerOpen(false); setSelectedPlayer(null) }}
        player={selectedPlayer}
      />

      <DeleteModal
        open={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setPlayerToDelete(null) }}
        title='Delete Player'
        subtitle={`Are you sure you want to delete ${playerToDelete?.personalData?.firstName} ${playerToDelete?.personalData?.lastName}?`}
        buttonLabel={translate('ra.action.delete') || 'Delete'}
        handleDelete={() => playerToDelete && deletePlayerMutation.mutate(playerToDelete.id)}
        loading={deletePlayerMutation.isLoading}
      />
    </div>
  )
}

const PlayersPage = () => {
  const selectedOrg = JSON.parse(localStorage.getItem(localStorageIds.SELECTED_ORG) || 'null')

  if (
    selectedOrg?.type === ORGANISATION_TYPES.ORG_FEDERATION ||
    selectedOrg?.type === ORGANISATION_TYPES.ORG_COMPETITION // remove when competition orgs are dropped
  ) {
    return <OrgLevelPlayersPage />
  }
  return <LeaguePlayersPage />
}

export default PlayersPage
